import { defineStore } from 'pinia';

const STORAGE_KEY = 'cms-admin.theme';

export type ThemeMode = 'system' | 'light' | 'dark';

/**
 * UI theme preference. Three modes:
 *   - 'system' (default) — follow the OS prefers-color-scheme, live.
 *   - 'light' / 'dark'   — explicit override, remembered across reloads.
 *
 * The actual `.dark` class on <html> is applied here AND by the inline
 * boot script in index.html (which runs before the bundle to avoid a
 * light→dark flash). This store re-applies on init so the two stay in
 * sync, then owns every change after boot. Persisted to localStorage; the
 * choice is per-browser, not server-side.
 */
const mql = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

function readStored(): ThemeMode {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

/** Whether the given mode resolves to dark right now (system → OS). */
function resolvesDark(mode: ThemeMode): boolean {
    if (mode === 'dark') return true;
    if (mode === 'light') return false;
    return mql?.matches ?? false;
}

export const useThemeStore = defineStore('theme', {
    state: () => ({
        mode: readStored() as ThemeMode,
        /** Whether dark is currently applied — drives toggle icons/labels. */
        isDark: false,
    }),

    actions: {
        /**
         * Apply the current mode to <html> and start listening for OS
         * changes (only relevant while mode === 'system'). Call once at app
         * boot, after Pinia is available.
         */
        init() {
            this.apply();
            // Re-apply when the OS theme flips, but only if we're following
            // it. The listener stays registered for the app's lifetime; the
            // mode check inside makes it a no-op when an explicit override
            // is active.
            mql?.addEventListener('change', () => {
                if (this.mode === 'system') {
                    this.apply();
                }
            });
        },

        setMode(mode: ThemeMode) {
            this.mode = mode;
            localStorage.setItem(STORAGE_KEY, mode);
            this.apply();
        },

        /** Cycle System → Light → Dark → System, for a single-button toggle. */
        cycle() {
            const next: ThemeMode =
                this.mode === 'system' ? 'light'
                    : this.mode === 'light' ? 'dark'
                        : 'system';
            this.setMode(next);
        },

        apply() {
            const dark = resolvesDark(this.mode);
            document.documentElement.classList.toggle('dark', dark);
            this.isDark = dark;
        },
    },
});
